
import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

interface ReportConfig {
  type: 'sales' | 'customers' | 'products' | 'financial';
  period: 'week' | 'month' | 'quarter' | 'year' | 'custom';
  format: 'pdf' | 'excel' | 'csv';
  filters?: {
    productCategories?: string[];
    customerSegments?: string[];
    regions?: string[];
  };
  metrics?: string[];
}

interface GeneratedReport {
  id: string;
  title: string;
  data: any[];
  summary: {
    totalRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    topProduct: string;
    growthRate: number;
  };
  charts: Array<{
    type: 'line' | 'bar' | 'pie';
    title: string;
    data: any[];
  }>;
  insights: string[];
  recommendations: string[];
  generatedAt: string;
}

export const useAdvancedReporting = () => {
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: reportData, refetch } = useQuery({
    queryKey: ['advanced-report', reportConfig],
    queryFn: async (): Promise<GeneratedReport | null> => {
      if (!reportConfig) return null;

      const endDate = new Date();
      let startDate: Date;

      switch (reportConfig.period) {
        case 'week':
          startDate = startOfWeek(endDate);
          break;
        case 'month':
          startDate = startOfMonth(endDate);
          break;
        case 'quarter':
          startDate = subDays(endDate, 90);
          break;
        case 'year':
          startDate = subDays(endDate, 365);
          break;
        default:
          startDate = subDays(endDate, 30);
      }

      // Fetch data based on report type
      const { data: orders } = await supabase
        .from('orders')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const { data: products } = await supabase
        .from('products')
        .select('*');

      const { data: customers } = await supabase
        .from('profiles')
        .select('*');

      if (!orders || !products || !customers) {
        throw new Error('Failed to fetch data');
      }

      // Generate report based on type
      const totalRevenue = orders.reduce((sum, order) => sum + Number(order.total_amount), 0);
      const totalOrders = orders.length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      // Find top product (simplified)
      const topProduct = products[0]?.name || 'N/A';

      // Calculate growth rate (mock calculation)
      const growthRate = Math.random() * 20 - 10; // -10% to +10%

      // Generate charts data
      const dailyData = orders.reduce((acc: any, order) => {
        const date = format(new Date(order.created_at), 'MMM dd');
        acc[date] = (acc[date] || 0) + Number(order.total_amount);
        return acc;
      }, {});

      const chartData = Object.entries(dailyData).map(([date, revenue]) => ({
        date,
        revenue
      }));

      // Generate insights and recommendations
      const insights = [
        totalRevenue > 1000 ? 'Revenue is performing well above average' : 'Revenue needs attention',
        `${totalOrders} orders processed during this period`,
        averageOrderValue > 50 ? 'Customers have high purchase value' : 'Focus on increasing order value'
      ];

      const recommendations = [
        'Consider seasonal promotions to boost sales',
        'Optimize product recommendations for higher AOV',
        'Implement customer retention strategies'
      ];

      return {
        id: `report_${Date.now()}`,
        title: `${reportConfig.type.charAt(0).toUpperCase() + reportConfig.type.slice(1)} Report - ${reportConfig.period}`,
        data: orders,
        summary: {
          totalRevenue,
          totalOrders,
          averageOrderValue,
          topProduct,
          growthRate
        },
        charts: [
          {
            type: 'line',
            title: 'Revenue Trend',
            data: chartData
          },
          {
            type: 'bar',
            title: 'Daily Orders',
            data: chartData.map(d => ({ ...d, orders: Math.floor(Math.random() * 10) + 1 }))
          }
        ],
        insights,
        recommendations,
        generatedAt: new Date().toISOString()
      };
    },
    enabled: !!reportConfig,
  });

  const generateReport = useCallback(async (config: ReportConfig) => {
    setIsGenerating(true);
    setReportConfig(config);
    
    // Simulate processing time
    setTimeout(() => {
      setIsGenerating(false);
      refetch();
    }, 2000);
  }, [refetch]);

  const downloadReport = useCallback((report: GeneratedReport, format: 'pdf' | 'excel' | 'csv') => {
    // Simulate download
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.title.replace(/\s+/g, '_')}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const scheduleReport = useCallback((config: ReportConfig, schedule: 'daily' | 'weekly' | 'monthly') => {
    // This would integrate with a scheduling service
    // Scheduled report configuration saved
    return `report_schedule_${Date.now()}`;
  }, []);

  return {
    reportData,
    isGenerating,
    generateReport,
    downloadReport,
    scheduleReport,
    clearReport: () => setReportConfig(null)
  };
};
